import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import DividerGhostButton from '@wix/da-ds/pkg/Buttons/DividerGhostButton';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import { EditorState } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import { fromDocument } from '@wix/da-editor-tiptap/pkg/components/utils/helpers';
import QuestionCard from '../../../OriginalDeviationPopup/_partials/SecondStep/_partials/QuestionCard';
import QuestionForm from '../../../OriginalDeviationPopup/_partials/SecondStep/_partials/QuestionForm';
import {
  AnCQuestionWithEditorState,
  AnCUnsavedQuestion,
  AnCQuestionFieldName,
  AnCQuestionFormFields,
  ArtsAndCraftsDeviationFieldName,
  ArtsAndCraftsDeviationFormFields,
} from '../../types';
import ShippingInformationCard from './_partials/ShippingInformationCard';

import s from './SecondStep.scss';

const MAX_QUESTIONS = 25;

interface Props {
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
  hasError?: boolean;
}

const SecondStep: React.FC<Props> = ({ scrollContainerRef }) => {
  const { t } = useTranslation();
  const [addingQuestionPosition, setAddingQuestionPosition] = useState<number | null>(null);
  const [editingQuestionPosition, setEditingQuestionPosition] = useState<number | null>(null);
  const questionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const addSlotRef = useRef<HTMLDivElement | null>(null);
  const { control } = useFormContext<ArtsAndCraftsDeviationFormFields>();
  const {
    field: { value: questions, onChange },
  } = useController({
    name: ArtsAndCraftsDeviationFieldName.QUESTIONS,
    control,
  });

  const formDataToQuestion = (formData: AnCQuestionFormFields): AnCUnsavedQuestion => {
    const questionEditor = formData[AnCQuestionFieldName.QUESTION_BODY];
    const editorState = JSON.parse(questionEditor()) as EditorState;

    const answers = formData[AnCQuestionFieldName.ANSWER_CHOICES];
    if (answers && answers.length > 0) {
      answers.forEach((value, index) => {
        if (`${value.priceModifier}` === '') {
          delete value.priceModifier;
        }
        answers[index] = value;
      });
    }

    return {
      type: formData[AnCQuestionFieldName.QUESTION_TYPE],
      isRequired: formData[AnCQuestionFieldName.IS_REQUIRED],
      answers,
      question: fromDocument(editorState.document),
    };
  };

  const addQuestion = useCallback(
    (formData: AnCQuestionFormFields) => {
      if (addingQuestionPosition === null || questions.length >= MAX_QUESTIONS) {
        return;
      }
      const newQuestions = [...questions];
      newQuestions.splice(addingQuestionPosition, 0, formDataToQuestion(formData));
      onChange(newQuestions);
      setAddingQuestionPosition(null);
    },
    [questions, addingQuestionPosition, onChange]
  );

  const updateQuestion = useCallback(
    (position: number, formData: AnCQuestionFormFields) => {
      onChange(
        questions.map((question, idx) =>
          idx === position ? formDataToQuestion(formData) : question
        )
      );
      setEditingQuestionPosition(null);
    },
    [questions, onChange]
  );

  const removeQuestion = useCallback(
    (position: number) => {
      onChange(questions.filter((_, idx) => idx !== position));
    },
    [questions, onChange]
  );

  const moveQuestion = useCallback(
    (currentPosition: number, newPosition: number) => {
      const newQuestions = [...questions];
      const [movedQuestion] = newQuestions.splice(currentPosition, 1);
      newQuestions.splice(newPosition, 0, movedQuestion);
      onChange(newQuestions);
    },
    [questions, onChange]
  );

  const onAddQuestionClick = useCallback(
    (position: number) => {
      if (addingQuestionPosition === null && questions.length < MAX_QUESTIONS) {
        setAddingQuestionPosition(position);
      }
    },
    [addingQuestionPosition, questions.length]
  );

  const onEditQuestionClick = useCallback(
    (position: number) => {
      if (addingQuestionPosition === null) {
        setEditingQuestionPosition(position);
      }
    },
    [addingQuestionPosition]
  );

  useEffect(() => {
    const scrollEl =
      editingQuestionPosition !== null
        ? questionRefs.current[editingQuestionPosition]
        : addingQuestionPosition !== null
          ? addingQuestionPosition === 0
            ? addSlotRef.current
            : questionRefs.current[addingQuestionPosition - 1]
          : null;
    if (scrollEl) {
      requestAnimationFrame(() => {
        scrollEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }, [editingQuestionPosition, addingQuestionPosition]);

  const isFormOpen = addingQuestionPosition !== null || editingQuestionPosition !== null;

  const renderQuestionFormOrButton = (position: number) => {
    if (addingQuestionPosition === position) {
      return (
        <QuestionForm
          scrollContainerRef={scrollContainerRef}
          onSave={formData => addQuestion(formData as unknown as AnCQuestionFormFields)}
          onDiscard={() => setAddingQuestionPosition(null)}
        />
      );
    }
    if (questions.length >= MAX_QUESTIONS) {
      return null;
    }
    return (
      <DividerGhostButton
        onClick={() => onAddQuestionClick(position)}
        size="medium"
        disabled={isFormOpen}
        icon={AddIcon}
      >
        {t('artsAndCraftsDeviationPopup.questionnaire.addQuestion')}
      </DividerGhostButton>
    );
  };

  return (
    <div className={s['root']}>
      <p className={s['subheading']}>{t('artsAndCraftsDeviationPopup.questionnaire.subheading')}</p>
      <ShippingInformationCard />
      {questions.length >= MAX_QUESTIONS && (
        <p className={s['error']}>
          {t('artsAndCraftsDeviationPopup.questionnaire.maxQuestionsReached')}
        </p>
      )}
      <div ref={addSlotRef}>{renderQuestionFormOrButton(0)}</div>

      <div className={s['questions']}>
        {(questions as AnCQuestionWithEditorState[]).map(
          (question: AnCUnsavedQuestion, idx: number) => (
            <div
              className={s['question']}
              key={`question-${idx}`}
              ref={el => {
                questionRefs.current[idx] = el;
              }}
            >
              {editingQuestionPosition === idx ? (
                <QuestionForm
                  scrollContainerRef={scrollContainerRef}
                  question={question as any}
                  onSave={formData =>
                    updateQuestion(idx, formData as unknown as AnCQuestionFormFields)
                  }
                  onDiscard={() => setEditingQuestionPosition(null)}
                />
              ) : (
                <QuestionCard
                  question={question as any}
                  hasMoveUp={idx > 0}
                  hasMoveDown={idx < questions.length - 1}
                  onMoveUpClick={() => moveQuestion(idx, idx - 1)}
                  onMoveDownClick={() => moveQuestion(idx, idx + 1)}
                  onEditClick={() => onEditQuestionClick(idx)}
                  onRemoveClick={() => removeQuestion(idx)}
                  disableActions={isFormOpen}
                />
              )}
              {renderQuestionFormOrButton(idx + 1)}
            </div>
          )
        )}
      </div>
    </div>
  );
};
SecondStep.displayName = 'SecondStep';

export default SecondStep;
